import React, { useState } from 'react'
import { Modal, Form, Input, Typography } from 'antd'
import { CloseCircleOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'

import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { DisplayLeaveDuration } from '../display-leave-in-days-and-hours'
import { isToilLeave } from '../../functions/is-toil-leave-request'
import { convertHourFormats } from '../../functions/convert-between-hour-formats'
import { formatTimeString } from '../../functions/get-days-or-hours'
import { HourFormatEnum } from '../../types/user'

const { Text } = Typography

interface IDenyWithReasonFormProps {
  visibleModal: boolean
  handleCancel: () => void
  onSave: (statusReason: string) => Promise<void>
  /** Deny flow has always let approvers submit without a reason. Keep the default; override to
   * true only if a surface needs to force a non-empty reason. */
  reasonRequired?: boolean
  /** Full leave record; when provided the modal renders the leave-info header (type dot,
   * leave type name, user name, period) matching the Dashboard deny modal. */
  leaveRequest?: any
  /** Needed to render part-day leave hours in the viewer's 12/24h preference. Defaults to 24h. */
  hourFormat?: HourFormatEnum
}

const DenyWithReasonForm = ({
  visibleModal,
  handleCancel,
  onSave,
  reasonRequired = false,
  leaveRequest,
  hourFormat = HourFormatEnum.twentyFour,
}: IDenyWithReasonFormProps): React.ReactElement => {
  const [form] = Form.useForm<{ statusReason: string }>()
  const [isLoading, setIsLoading] = useState(false)
  const { TextArea } = Input
  const intl = useIntl()

  const lr = leaveRequest as any
  const isToil = lr?.id ? isToilLeave(lr.id) : false
  const leaveTypeName: string | undefined = lr?.leaveType?.name ?? lr?.leavePolicy?.leaveType?.name
  const leaveTypeColor: string | undefined = lr?.leaveType?.color ?? lr?.leavePolicy?.leaveType?.color
  const userName: string | undefined = lr?.user?.name
  const startDate: string | undefined = lr?.startDate
  const endDate: string | undefined = lr?.endDate
  const workingDays: number = lr?.workingDays ?? 0
  const daysList = lr?.daysList
  const hasWorkingHoursSchedule = Boolean(lr?.hasWorkingHoursSchedule)
  const isPartDay: boolean = Boolean(lr?.isPartDay)
  const partDay = lr?.partDay
  const partDayStartHour: number | undefined = lr?.partDayStartHour
  const partDayEndHour: number | undefined = lr?.partDayEndHour

  // Part-day leaves show "Aug 22, 08:15 – 12:15 · duration" - same split as LeavesBox.
  let convertedStartHour: string | undefined
  let convertedEndHour: string | undefined
  if (isPartDay && (partDay || (typeof partDayStartHour === 'number' && typeof partDayEndHour === 'number'))) {
    const {
      value: startHourValue,
      minute: startMinute,
      amOrPm: startAmOrPm,
    } = convertHourFormats(hourFormat, partDay ? partDay.startHour : (partDayStartHour as number), partDay ? (partDay.startMinute as number) : 0)
    const {
      value: endHourValue,
      minute: endMinute,
      amOrPm: endAmOrPm,
    } = convertHourFormats(hourFormat, partDay ? partDay.endHour : (partDayEndHour as number), partDay ? (partDay.endMinute as number) : 0)
    const showZeroPadding = startMinute > 0 || endMinute > 0
    convertedStartHour = formatTimeString({ hour: startHourValue, minute: startMinute, showZeroPadding, hourFormat, amOrPm: startAmOrPm })
    convertedEndHour = formatTimeString({ hour: endHourValue, minute: endMinute, showZeroPadding, hourFormat, amOrPm: endAmOrPm })
  }

  const onFinish = () => {
    form
      .validateFields()
      .then(async (values) => {
        setIsLoading(true)
        try {
          await onSave(values.statusReason ?? '')
        } finally {
          setIsLoading(false)
        }
      })
      .catch(() => undefined)
  }

  return (
    <Modal
      title={<IntlMessages id={isToil ? 'app.denyToilRequest' : 'app.denyLeaveRequest'} />}
      open={visibleModal}
      closeIcon={<CloseCircleOutlined />}
      onCancel={handleCancel}
      confirmLoading={isLoading}
      okText={<IntlMessages id="app.deny" />}
      onOk={onFinish}
      okButtonProps={{ type: 'primary', danger: true }}
      cancelText={<IntlMessages id="app.cancel" />}
    >
      {(leaveTypeName || userName || startDate) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 8px' }}>
            {leaveTypeName && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {leaveTypeColor && (
                  <span
                    aria-hidden="true"
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 4,
                      background: leaveTypeColor,
                      flexShrink: 0,
                    }}
                  />
                )}
                <Text style={{ fontSize: 14, fontWeight: 600 }}>{leaveTypeName}</Text>
              </span>
            )}
            {leaveTypeName && userName && (
              <Text type="secondary" style={{ fontSize: 14 }}>
                ·
              </Text>
            )}
            {userName && <Text style={{ fontSize: 14, fontWeight: 600 }}>{userName}</Text>}
          </div>
          {startDate && endDate && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <IntlMessages
                id={
                  isPartDay && convertedStartHour && convertedEndHour
                    ? 'components.leaveBox.leavesBoxHalfDayPending'
                    : startDate === endDate
                      ? 'components.leaveBox.leavesPendingOneDay'
                      : 'components.leaveBox.leavesPendingInfo'
                }
                values={{
                  leaveStartDate: intl.formatDate(dayjs(startDate).toDate(), { month: 'short', day: 'numeric' }),
                  leaveEndDate: intl.formatDate(dayjs(endDate).toDate(), { month: 'short', day: 'numeric' }),
                  startHour: convertedStartHour,
                  endHour: convertedEndHour,
                  duration: <DisplayLeaveDuration value={workingDays} daysList={daysList} hasSchedule={hasWorkingHoursSchedule} />,
                }}
              />
            </Text>
          )}
        </div>
      )}
      <Form key="denyWithReasonForm" form={form} name="denyWithReasonForm" onFinish={onFinish} layout="vertical">
        <Form.Item
          name="statusReason"
          label={
            <Text style={{ fontSize: 14, fontWeight: 600 }}>
              {reasonRequired ? <IntlMessages id="components.denyWithReasonForm.reason" /> : <IntlMessages id="components.denyWithReasonForm.reasonOptional" />}
            </Text>
          }
          rules={reasonRequired ? [{ required: true, message: <IntlMessages id="error.fieldIsRequired" /> }] : undefined}
          extra={
            !reasonRequired && userName ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {intl.formatMessage({ id: 'components.denyWithReasonForm.reasonHelper' }, { name: userName })}
              </Text>
            ) : undefined
          }
        >
          <TextArea rows={3} style={{ width: '100%' }} autoComplete="off" />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default DenyWithReasonForm
